import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import ResourcesFooter from "@/components/ResourcesFooter";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    if (user.role === "MEMBER") return <Navigate to="/me" replace />;
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role === "MEMBER") navigate("/me");
      else navigate("/");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-bg min-h-screen flex">
      <div
        className="hidden md:block md:w-1/2 relative"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1533746228171-962520811097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxjYW1lcmElMjBsZW5zJTIwZGFya3xlbnwwfHx8fDE3ODA1ODE4ODF8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.85) 100%)",
          }}
        />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">
            Camera Club Score
          </div>
          <h1 className="text-4xl font-semibold leading-tight text-white max-w-md">
            Instantly available.<br />Competition Preparation.<br />Season-long memory.
          </h1>
          <p className="text-neutral-400 mt-4 max-w-md text-base leading-relaxed">
            A keyboard-first scoring console built for the rhythm of a real club night.
          </p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3 md:hidden">
            Camera Club Score
          </div>
          <h2 className="text-3xl font-semibold mb-2">Sign in</h2>
          <p className="muted-text mb-8 text-sm">
            Use your club account to continue.
          </p>

          <div className="form-row">
            <label className="ccs-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="ccs-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              data-testid="login-email-input"
            />
          </div>

          <div className="form-row">
            <div className="flex items-center justify-between">
              <label className="ccs-label" htmlFor="password">Password</label>
              <a
                href="/forgot-password"
                className="text-amber-400 text-xs hover:text-amber-300"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </a>
            </div>
            <input
              id="password"
              type="password"
              className="ccs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
            />
          </div>

          {err && (
            <div
              className="text-sm danger-text mb-4 py-2 px-3"
              style={{ background: "rgba(127,29,29,0.2)", border: "1px solid #5b1e1e" }}
              data-testid="login-error"
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="ccs-btn w-full justify-center mt-2"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-4 text-sm text-center">
            <span className="muted-text">Don&rsquo;t have an account? </span>
            <a
              href="/register"
              className="text-amber-400 hover:text-amber-300 font-medium"
              data-testid="login-create-account-link"
            >
              Create one
            </a>
          </div>

          <ResourcesFooter />
        </form>
      </div>
    </div>
  );
}
